import {
  Button,
  Chip,
  ChipGroup,
  Modal,
  ModalVariant,
  Select,
  SelectOption,
  SelectVariant,
} from '@patternfly/react-core'
import { isValid, parseISO } from 'date-fns'
import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { PatientList } from '@app/components'
import { usePatientsSearch } from '@app/domain/patient/patient.hooks'
import type {
  IGetPatientOptions,
  IPatient} from '@app/domain/patient/patient.interface';
import {
  ProgramStartOption,
} from '@app/domain/patient/patient.interface'
import type { ISortDirection } from '@app/lib/sorting'
import { getLocalDate } from '@app/lib/time'

import type {
  SelectOptionObject} from '@patternfly/react-core';
import type { ReactElement} from 'react';

interface Props {
  confirmButtonLabel: string
  defaultSearchOptions?: Partial<IGetPatientOptions>
  isModalOpen: boolean
  onCancel: () => void
  onPatientsFound: (patient: IPatient[], startDate: string) => void
  validateSelectedPatient: (patient: IPatient) => null | ReactElement
}

export const SelectMultiplePatientModal = function ({
  confirmButtonLabel,
  defaultSearchOptions,
  isModalOpen,
  onCancel,
  onPatientsFound,
  validateSelectedPatient,
}: Props) {
  const { patients, totalNumberOfRecords, getPatients } = usePatientsSearch(
    defaultSearchOptions,
  )

  const [birthSexFilter, setBirthSexFilter] = useState<string[]>([])
  const [dateOfBirthFilter, setDateOfBirthFilter] = useState<string>(null)
  const [mrnInputFilter, setMrnInputFilter] = useState<string>(null)
  const [nameInputFilter, setNameInputFilter] = useState<string>(null)
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [recordsPerPage, setRecordsPerPage] = useState<number>(50)
  const [sortDirection, setSortDirection] = useState<ISortDirection>(null)
  const [sortField, setSortField] = useState<string>(null)

  const [selectedPatients, setSelectedPatients] = useState<IPatient[]>([])
  const [validationError, setValidationError] = useState<ReactElement>(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [selectedStartOption, setSelectedStartOption] = useState<
    ProgramStartOption
  >(ProgramStartOption.Now)

  const now = new Date()
  const defaultDate = `${getLocalDate(now)}T${now.toTimeString().slice(0, 5)}`
  const [startDate, setStartDate] = useState<string>(defaultDate)

  const onRefresh = useCallback(() => {
    getPatients(pageNumber, recordsPerPage, {
      birthSex: birthSexFilter,
      dateOfBirth: dateOfBirthFilter,
      mrn: mrnInputFilter,
      patientName: nameInputFilter,
      sortDirection: sortDirection,
      sortField: sortField,
    })
  }, [
    birthSexFilter,
    dateOfBirthFilter,
    getPatients,
    mrnInputFilter,
    nameInputFilter,
    pageNumber,
    recordsPerPage,
    sortDirection,
    sortField,
  ])

  useEffect(() => {
    onRefresh()
  }, [onRefresh])

  const onBirthSexFilterUpdated = useCallback((updatedBirthSex) => {
    setBirthSexFilter(updatedBirthSex)
    setPageNumber(1)
  }, [])

  const onDateOfBirthFilterUpdated = useCallback((updatedDateOfBirth) => {
    setDateOfBirthFilter(updatedDateOfBirth)
    setPageNumber(1)
  }, [])

  const onMrnFilterUpdated = useCallback((updatedMrn) => {
    setMrnInputFilter(updatedMrn)
    setPageNumber(1)
  }, [])

  const onNameFilterUpdated = useCallback((updatedName) => {
    setNameInputFilter(updatedName)
    setPageNumber(1)
  }, [])

  const onSetPage = useCallback((updatedPageNumber) => {
    setPageNumber(updatedPageNumber)
  }, [])

  const onPerPageSelect = useCallback((updatedRecordsPerPage) => {
    setRecordsPerPage(updatedRecordsPerPage)
    setPageNumber(1)
  }, [])

  const onSetSort = useCallback(
    (newField: string, newDirection: ISortDirection) => {
      setPageNumber(1)
      setSortField(newField)
      setSortDirection(newDirection)
    },
    [],
  )

  const isValidDateTime = (value: string): boolean => {
    if (!value) return false
    const parsedDate = parseISO(value)
    return isValid(parsedDate)
  }

  const handleStartDateChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newDate = event.target.value

      if (!newDate) {
        return
      }

      if (isValidDateTime(newDate)) {
        setStartDate(newDate)
      }
    },
    [],
  )

  const handleStartOptionChange = useCallback(
    (
      _event: React.MouseEvent | React.ChangeEvent,
      selection: string | SelectOptionObject,
    ) => {
      const selectedValue = selection as ProgramStartOption

      setSelectedStartOption(selectedValue)
      setIsDropdownOpen(false)

      if (selectedValue === ProgramStartOption.Now) {
        setStartDate(defaultDate)
      }
    },
    [defaultDate],
  )

  const onClearAllFilters = useCallback(() => {
    setBirthSexFilter([])
    setDateOfBirthFilter(null)
    setMrnInputFilter(null)
    setNameInputFilter(null)
    setPageNumber(1)
  }, [setPageNumber])

  const handlePatientSelected = useCallback(
    (patient: IPatient) => {
      let updatedSelectedPatients = []
      if (selectedPatients.some((o) => o.id === patient.id)) {
        updatedSelectedPatients = selectedPatients.filter(
          (item) => item.id !== patient.id,
        )
      } else {
        const errorElement = validateSelectedPatient(patient)
        if (errorElement) {
          setValidationError(errorElement)
        } else {
          updatedSelectedPatients = selectedPatients.concat(patient)
        }
      }

      setSelectedPatients(updatedSelectedPatients)
    },
    [selectedPatients, validateSelectedPatient],
  )

  const handleConfirm = useCallback(() => {
    setSelectedPatients([])
    onPatientsFound(selectedPatients, startDate)
  }, [onPatientsFound, selectedPatients, startDate])

  const handleCancel = useCallback(() => {
    setSelectedPatients([])
    setValidationError(null)
    onCancel()
  }, [onCancel])

  const filters = useMemo(() => {
    return {
      birthSexFilter,
      dateOfBirthFilter,
      mrnFilter: mrnInputFilter,
      nameFilter: nameInputFilter,
      onBirthSexFilterUpdated,
      onDateOfBirthFilterUpdated,
      onMrnFilterUpdated,
      onNameFilterUpdated,
    }
  }, [
    birthSexFilter,
    dateOfBirthFilter,
    mrnInputFilter,
    nameInputFilter,
    onBirthSexFilterUpdated,
    onDateOfBirthFilterUpdated,
    onMrnFilterUpdated,
    onNameFilterUpdated,
  ])

  const pagination = useMemo(() => {
    return {
      onPerPageSelect,
      onSetPage,
      pageNumber,
      recordsPerPage,
      totalNumberOfRecords,
    }
  }, [
    onPerPageSelect,
    onSetPage,
    pageNumber,
    recordsPerPage,
    totalNumberOfRecords,
  ])

  const sorting = useMemo(() => {
    return {
      onSetSort,
      sortDirection,
      sortField,
    }
  }, [onSetSort, sortDirection, sortField])

  return (
    <Modal
      variant={ModalVariant.large}
      title="Select Patients"
      isOpen={isModalOpen}
      onClose={handleCancel}
      actions={[
        <Button
          key="confirm"
          variant="primary"
          onClick={handleConfirm}
          isDisabled={selectedPatients.length === 0}
        >
          {confirmButtonLabel}
        </Button>,
        <Button key="cancel" variant="link" onClick={handleCancel}>
          Cancel
        </Button>,
      ]}
    >
      {validationError}

      <PatientList
        onPatientSelected={handlePatientSelected}
        patients={patients}
        onRefresh={onRefresh}
        filters={filters}
        pagination={pagination}
        selectedPatients={selectedPatients}
        sorting={sorting}
        onClearAllFilters={onClearAllFilters}
      />

      <ChipGroup categoryName="Selected Patients">
        {selectedPatients.map((patient) => (
          <Chip key={patient.id} onClick={() => handlePatientSelected(patient)}>
            {`${patient.familyName}, ${patient.givenNames}`}
          </Chip>
        ))}
      </ChipGroup>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          marginTop: '1rem',
        }}
      >
        <label htmlFor="start-date-option" style={{ whiteSpace: 'nowrap' }}>
          Program Start Date*
        </label>

        <div style={{ minWidth: '150px' }}>
          <Select
            variant={SelectVariant.single}
            onToggle={setIsDropdownOpen}
            onSelect={handleStartOptionChange}
            selections={selectedStartOption}
            isOpen={isDropdownOpen}
            menuAppendTo="parent"
            style={{ width: '100%' }}
          >
            <SelectOption value={ProgramStartOption.Now}>
              {ProgramStartOption.Now}
            </SelectOption>
            <SelectOption value={ProgramStartOption.Custom}>
              {ProgramStartOption.Custom}
            </SelectOption>
          </Select>
        </div>

        {selectedStartOption === ProgramStartOption.Custom && (
          <input
            id="start-date"
            type="datetime-local"
            value={startDate}
            onChange={handleStartDateChange}
            required
            style={{ flex: '1', minWidth: '200px', maxWidth: '250px' }}
          />
        )}
      </div>
    </Modal>
  )
}
